import { updatePendingThreadCount } from 'actions/messaging'
import AddUserIcon from 'components/ReusableComponents/Icons/AddUser'
import AnalyticsIcon from 'components/ReusableComponents/Icons/Analytics'
import AnnouncementIcon from 'components/ReusableComponents/Icons/Announcement'
import ArrivalsIcon from 'components/ReusableComponents/Icons/Arrivals'
import BeforeStayIcon from 'components/ReusableComponents/Icons/BeforeStay'
import OccupancyIcon from 'components/ReusableComponents/Icons/CalendarDotted'
import CampaignAnalysisIcon from 'components/ReusableComponents/Icons/CampaignAnalysis'
import ChatIcon from 'components/ReusableComponents/Icons/Chat'
import ChatBotIcon from 'components/ReusableComponents/Icons/ChatBot'
import ComplianceIcon from 'components/ReusableComponents/Icons/Compliance'
import ContractIcon from 'components/ReusableComponents/Icons/Contract'
import DirectMessage from 'components/ReusableComponents/Icons/DirectMessage'
import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import FeedbackIcon from 'components/ReusableComponents/Icons/Feedback'
import HotelSetupIcon from 'components/ReusableComponents/Icons/HotelSetup'
import InPropertyIcon from 'components/ReusableComponents/Icons/InProperty'
import InviteIcon from 'components/ReusableComponents/Icons/Invite'
import InvoiceIcon from 'components/ReusableComponents/Icons/Invoice'
import MapIcon from 'components/ReusableComponents/Icons/Map'
import GraphicIcon from 'components/ReusableComponents/Icons/MessagingPerformance'
import MonthlyRevenue from 'components/ReusableComponents/Icons/MonthlyRevenue'
import NotificationIcon from 'components/ReusableComponents/Icons/Notification'
import ReservationIcon from 'components/ReusableComponents/Icons/Reservation'
import RestrictedDateIcon from 'components/ReusableComponents/Icons/RestrictedDate'
import ReviewsIcon from 'components/ReusableComponents/Icons/Reviews'
import ROIIcon from 'components/ReusableComponents/Icons/ROI'
import SiteAbandonment from 'components/ReusableComponents/Icons/SiteAbandonment'
import TextBlastIcon from 'components/ReusableComponents/Icons/TextBlast'
import UsageStatsIcon from 'components/ReusableComponents/Icons/UsageStats'
import UserManagementIcon from 'components/ReusableComponents/Icons/UserManagement'
import SidebarDirectfulTestLogo from 'components/ReusableComponents/SidebarDirectfulTester'
import { userPositions } from 'features/ManageUserDialog/userTitleTypes'
import { userRoleLevels, userRoles } from 'features/UserPermissions'
import { useInternalAdmin } from 'hooks'
import useResortNearDisney from 'hooks/useResortNearDisney'
import { PMSs } from 'main/hotelBrandsAndChains'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useHistory, useLocation } from 'react-router-dom'

import { selectCurrentHotelPMS, selectIsInternalAdmin } from '../accessors/index'
import CurrentHotelWithSelector from './ReusableComponents/CurrentHotelWithSelector/index'
import CSMListIcon from './ReusableComponents/Icons/CSMList'
import DefaultCampaignsIcon from './ReusableComponents/Icons/DefaultCampaignsIcon'
import EmailIcon from './ReusableComponents/Icons/EmailIcon'
import { HealthIcon } from './ReusableComponents/Icons/Health'
import UploadReportIcon from './ReusableComponents/Icons/Upload'

const Link = ({
  children,
  to,
  adminOnly = false,
  devOnly = false,
  operaOnly = false,
  icon
}) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const pms = useSelector(selectCurrentHotelPMS)

  if (devOnly && !document.location.host.match(/dev-|localhost/)) {
    return null
  }

  if (adminOnly && !isInternalAdmin) {
    return null
  }

  if (operaOnly && pms !== 'opera') {
    return null
  }

  if (typeof icon === 'string') {
    return (
      <li>
        <NavLink to={to}>
          <i className={icon} data-tip={children}></i>
          <span>{children}</span>
        </NavLink>
      </li>
    )
  }

  const Icon = icon

  return (
    <li>
      <NavLink to={to}>
        {Icon && <Icon className="sidebar-svg-icon" />}
        <span>{children}</span>
      </NavLink>
    </li>
  )
}

const SidebarFunc = ({ noCollapse }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const roles = useSelector(state => state.login.roles)
  const position = useSelector(state => state.login.position)

  const localVersion = useSelector(s => s.versionInfo.localVersion)

  const pendingSalesThreadsCountFromApi = useSelector(
    s => s.salesMessaging.pendingThreadsCount
  )
  const salesThreads = useSelector(s => s.salesMessaging.threads)
  const pendingSalesThreadsCount =
    Object.keys(salesThreads || {}).length > 0
      ? Object.values(salesThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingSalesThreadsCountFromApi
  const pendingGdsThreadsCount = useSelector(s => s.salesMessaging.pendingGdsThreadsCount)
  const pendingBookingCount = useSelector(s => s.salesMessaging.pendingBookingCount)

  const isInviteBackDisabled = useSelector(s => s.hotelDetails.inviteBackIsDisabled)

  const isIndependentlyManaged = useSelector(s => s.hotelDetails.groupId) === -100

  const isPreOnboardingCompleted = useSelector(
    s => s.hotelDetails.isPreOnboardingCompleted
  )

  const pendingServiceThreadsCountFromApi = useSelector(
    s => s.serviceMessaging.pendingThreadsCount
  )
  const serviceThreads = useSelector(s => s.serviceMessaging.threads)
  const pendingServiceThreadsCount =
    Object.keys(serviceThreads || {}).length > 0
      ? Object.values(serviceThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingServiceThreadsCountFromApi

  const pendingReviewThreadsCountFromApi = useSelector(
    s => s.reviewMessaging.pendingThreadsCount
  )
  const reviewThreads = useSelector(s => s.reviewMessaging.threads)
  const pendingReviewThreadsCount =
    Object.keys(reviewThreads || {}).length > 0
      ? Object.values(reviewThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingReviewThreadsCountFromApi

  const pendingDirectThreadsCountFromApi = useSelector(
    s => s.directMessaging.pendingThreadsCount
  )
  const directThreads = useSelector(s => s.directMessaging.threads)
  const pendingDirectThreadsCount =
    Object.keys(directThreads || {}).length > 0
      ? Object.values(directThreads)
          .filter(t => t.status === 'ClientWaiting')
          .reduce((sum, t) => sum + (t.waitingMessageCount || 0), 0)
      : pendingDirectThreadsCountFromApi

  const isInternalAdmin = useInternalAdmin()

  const pms = useSelector(state => state.hotelDetails.pms)

  const hasDisconnectedPMS = pms === PMSs.disconnected
  const hasPepPMS = pms === PMSs.pep

  function canSeePage(role) {
    const positivePermissions = [
      userRoleLevels.HasGroupLevelPermission,
      userRoleLevels.HasPermission
    ]

    return positivePermissions.includes(roles[role])
  }

  const hasMessagingAccess =
    canSeePage(userRoles.ChatInProperty) ||
    canSeePage(userRoles.ChatSalesBefore) ||
    canSeePage(userRoles.ChatSalesAfter) ||
    canSeePage(userRoles.ChatDirect)

  function canSeeROIPage() {
    if (isInternalAdmin) {
      return true
    }
    if (!isIndependentlyManaged) {
      return roles[userRoles.Billing] === userRoleLevels.HasGroupLevelPermission
    }
    if (isIndependentlyManaged) {
      return (
        [userPositions.Corporate, userPositions.GeneralManager].includes(position) &&
        roles[userRoles.Billing] === userRoleLevels.HasPermission
      )
    }
    return false
  }

  const hasRoiAccess = canSeeROIPage()

  const dispatch = useDispatch()

  useEffect(() => {
    if (hasMessagingAccess) {
      dispatch(updatePendingThreadCount())
    }
  }, [dispatch, hasMessagingAccess])

  const history = useHistory()
  const location = useLocation()

  const isResortNearDisney = useResortNearDisney()

  return (
    <>
      <div
        onMouseEnter={() => setMobileMenuOpen(true)}
        onMouseLeave={() => setMobileMenuOpen(false)}
        className={`main-sidebar ${
          noCollapse ? '' : 'main-sidebar-collapsable'
        } mobile-menu-${mobileMenuOpen}`}
      >
        <div className="sidebarContainer">
          <CurrentHotelWithSelector isMobileMenuOpen={mobileMenuOpen} />
          <div className="sidebarScrollContainer">
            <Group>
              {!hasDisconnectedPMS && !isResortNearDisney && (
                <Link to="/arrivals" icon={ArrivalsIcon}>
                  Arrivals &amp; In-house
                </Link>
              )}
              {!isResortNearDisney && (
                <Link to="/bookings" icon={ReservationIcon}>
                  Bookings
                </Link>
              )}
              {isResortNearDisney && (
                <Link to="/gds-bookings" icon={ReservationIcon}>
                  Bookings
                </Link>
              )}
              {!isResortNearDisney && (
                <Link to="/gds-bookings" icon={ReservationIcon} adminOnly>
                  GDS bookings
                  <small
                    className="main-sidebar-count-bubble"
                    style={{
                      background: pendingBookingCount > 0 ? '#ee362c' : '#0079ff'
                    }}
                  >
                    {pendingBookingCount?.toString()}
                    <span
                      style={{
                        opacity: 0.5,
                        fontSize: 11,
                        marginTop: -2,
                        width: 10,
                        textAlign: 'center'
                      }}
                    >
                      |
                    </span>
                    {pendingGdsThreadsCount?.toString()}
                  </small>
                </Link>
              )}
              {hasDisconnectedPMS && (
                <Link to="/subscribers" icon={AddUserIcon}>
                  Recent subscriptions
                </Link>
              )}
              <Link to="/site-abandonment" icon={SiteAbandonment}>
                Site abandonment
              </Link>
            </Group>
            {!isResortNearDisney && hasMessagingAccess && (
              <Group name="Guest messaging">
                {canSeePage(userRoles.ChatInProperty) && (
                  <Link to="/messaging" icon={ChatIcon}>
                    During stay
                    {pendingServiceThreadsCount > 0 && (
                      <small className="main-sidebar-count-bubble">
                        {pendingServiceThreadsCount}
                      </small>
                    )}
                  </Link>
                )}
                {canSeePage(userRoles.ChatSalesBefore) && (
                  <Link to="/incoming" icon={BeforeStayIcon}>
                    Before stay
                    {pendingSalesThreadsCount > 0 && (
                      <small className="main-sidebar-count-bubble">
                        {pendingSalesThreadsCount}
                      </small>
                    )}
                  </Link>
                )}
                {canSeePage(userRoles.ChatSalesAfter) && (
                  <Link to="/feedback" icon={FeedbackIcon}>
                    Reviews
                    {pendingReviewThreadsCount > 0 && (
                      <small className="main-sidebar-count-bubble">
                        {pendingReviewThreadsCount}
                      </small>
                    )}
                  </Link>
                )}
                {canSeePage(userRoles.ChatDirect) && (
                  <Link to="/direct" icon={DirectMessage}>
                    Direct messages
                    {pendingDirectThreadsCount > 0 && (
                      <small className="main-sidebar-count-bubble">
                        {pendingDirectThreadsCount}
                      </small>
                    )}
                  </Link>
                )}
                {canSeePage(userRoles.ChatInProperty) && (
                  <Link to="/chatbot-responses" icon={ChatBotIcon}>
                    Chatbot responses
                  </Link>
                )}
              </Group>
            )}{' '}
            <Group name="Reports">
              {canSeePage(userRoles.ReportsView) && (
                <Link to="/analytics" icon={AnalyticsIcon}>
                  Analytics
                </Link>
              )}
              {location.pathname.includes('/analytics') &&
                !isResortNearDisney &&
                canSeePage(userRoles.ReportsView) && (
                  <div className="sub-navigation">
                    <Link to="/analytics/subscriptions">Subscriptions</Link>
                    <Link to="/analytics/outreach">Outreach</Link>
                    <Link to="/analytics/OTA-outreach">OTA Outreach</Link>
                    <Link to="/analytics/reviews">Reviews</Link>
                    <Link to="/analytics/conversions">Conversions</Link>
                  </div>
                )}
              {hasRoiAccess && (
                <Link to="/roi" icon={ROIIcon}>
                  Return on investment
                </Link>
              )}
              {!isResortNearDisney && canSeePage(userRoles.ReportsView) && (
                <Link to="/reviews" icon={ReviewsIcon}>
                  Review ratings
                </Link>
              )}
              {(roles[userRoles.Billing] === userRoleLevels.HasGroupLevelPermission ||
                roles[userRoles.BookingsView] ===
                  userRoleLevels.HasGroupLevelPermission ||
                roles[userRoles.BookingsManage] ===
                  userRoleLevels.HasGroupLevelPermission ||
                roles[userRoles.UserManage] === userRoleLevels.HasGroupLevelPermission ||
                isInternalAdmin) &&
                !isIndependentlyManaged && (
                  <Link to="/messaging-performance" icon={GraphicIcon}>
                    Messaging Performance
                  </Link>
                )}
            </Group>
            {canSeePage(userRoles.CampaignView) && !isResortNearDisney && (
              <Group name="Automated messages">
                <Link to="/manageCampaigns/sales" icon={InviteIcon}>
                  Invite messages{' '}
                  {isInviteBackDisabled && (
                    <ExclamationIcon
                      style={{
                        width: 19,
                        height: 19,
                        position: 'absolute',
                        right: 6,
                        background: 'white',
                        borderRadius: '50%',
                        fill: '#ff5e5e',
                        marginTop: 8
                      }}
                    />
                  )}
                </Link>
                <Link to="/manageCampaigns/service" icon={InPropertyIcon}>
                  In-property messages
                </Link>
                <Link to="/announcements" icon={AnnouncementIcon}>
                  Announcements
                </Link>
                <Link to="/custom-campaigns" icon={TextBlastIcon}>
                  Drip campaigns
                </Link>
              </Group>
            )}
            <Group name="Account">
              {hasPepPMS && (
                <Link to="/pms-reports" icon={UploadReportIcon}>
                  PMS Reports
                </Link>
              )}
              {canSeePage(userRoles.Billing) && (
                <Link to="/billing" icon={InvoiceIcon}>
                  Billing and payments
                </Link>
              )}
              {canSeePage(userRoles.UserManage) && (
                <Link to="/userPermissions" icon={UserManagementIcon}>
                  User Management
                </Link>
              )}
              <Link to="/compliance" icon={ComplianceIcon}>
                Compliance
              </Link>
              <Link to="/hotelDetails" icon="business-icon" adminOnly>
                Hotel details
              </Link>
              <Link to="/notificationPreferences" icon={NotificationIcon}>
                Configure notifications
              </Link>
            </Group>
            <Group adminOnly name="Admin" onDoubleClick={() => history.push('/debugApp')}>
              <Link to="/addGuestData" icon="upload-data-icon" adminOnly>
                Add guest data
              </Link>
              {!isPreOnboardingCompleted && (
                <Link to="/hotel-setup" icon={HotelSetupIcon}>
                  Hotel setup
                </Link>
              )}
              <Link to="/usage-stats" icon={UsageStatsIcon}>
                Usage stats
              </Link>
              <Link to="/occupancy" icon={OccupancyIcon}>
                Occupancy
              </Link>
              <Link to="/monthly-revenue" icon={MonthlyRevenue}>
                Monthly revenue
              </Link>
              <Link to="/monthly-ota-conversions" icon={MonthlyRevenue}>
                Monthly OTA conversions
              </Link>
              <Link to="/contracts" icon={ContractIcon}>
                Contracts
              </Link>
              <Link adminOnly to="/hotels" icon="hotels-icon">
                Hotels
              </Link>
              <Link adminOnly to="/hotels-by-csm" icon={CSMListIcon}>
                Manage CSM's
              </Link>
              <Link adminOnly to="/hotels-by-location" icon={MapIcon}>
                Hotels by location
              </Link>
              <Link to="/users" adminOnly icon="users-icon">
                Dashboard Users
              </Link>
              <Link to="/healthCheck/pms" icon={HealthIcon}>
                Health check
              </Link>
              <Link to="/defaultCampaigns/sales" icon={DefaultCampaignsIcon}>
                Default campaigns
              </Link>
              <Link to="/restricted-call-dates" icon={RestrictedDateIcon}>
                Restricted call dates
              </Link>
              <Link to="/emails" icon={EmailIcon}>
                Email logs
              </Link>
              <Link to="/dailyConversations" icon="ai-training-icon">
                Daily converations
              </Link>
              <Link to="/campaign-analysis" icon={CampaignAnalysisIcon}>
                Campaign analysis
              </Link>
              <Link to="/llm-platform" icon="ai-training-icon" adminOnly>
                LLM Platform
              </Link>
            </Group>
            {/* {!isResortNearDisney && (
              <Group>
                <Link to="/downloadApp" icon={AddAppIcon}>
                  Download mobile app
                </Link>
              </Group>
            )} */}
            <Group>
              <div
                style={{
                  padding: '2px 30px 6px',
                  fontSize: 13,
                  color: 'gray',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <SidebarDirectfulTestLogo />
                Dashboard version {localVersion}
              </div>
            </Group>
          </div>
        </div>
      </div>
    </>
  )
}

const Group = ({ name, children, adminOnly, onDoubleClick }) => {
  const isInternalAdmin = useSelector(selectIsInternalAdmin)

  if (!children) {
    return null
  }

  if (adminOnly && !isInternalAdmin) {
    return null
  }

  return (
    <div className="sidebar-group">
      {name && <h3 onDoubleClick={onDoubleClick}>{name.toUpperCase()}</h3>}
      <ul>{children}</ul>
    </div>
  )
}

export default SidebarFunc
